import Helpers from "@utils/Helpers";
import { Button, Divider, Row, Col, Input } from "antd";
import userApi from "services/UserApi";
import googleLogo from "@assets/icons/google.png";
import Validates from "@utils/Validates";
import Suggestion from "@components/Suggestion/Suggestion";
import { useEffect, useState } from "react";
import { errorLog } from "logging";

interface IInforForm {
  formSignUp: any;
  setCurrentStep: (step: number) => void;
  handleSetFormSignUp: any;
  googleLoginHook: any;
  googleId: string | null;
  setLoading: (isLoading: boolean) => void;
  setFormSignUp: (form: any) => void;
}

let debounceTimer: any;

const InfoForm: React.FC<IInforForm> = ({
  formSignUp,
  setCurrentStep,
  handleSetFormSignUp,
  googleLoginHook,
  googleId,
  setLoading,
  setFormSignUp,
}) => {
  const [isOpenSuggestion, setIsOpenSuggestion] = useState(false);
  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<any>({});
  const [addressSearchFailed, setAddressSearchFailed] = useState(false);

  // Coordinates are optional: the address fields below can be typed by hand
  // when the geocoder is unreachable (a blocked or failing provider must not
  // stop a registration), so only the text fields are required here.
  const validateForm = () => {
    if (
      formSignUp.first_name === "" ||
      formSignUp.last_name === "" ||
      formSignUp.email === "" ||
      formSignUp.street === "" ||
      formSignUp.city === "" ||
      formSignUp.province === "" ||
      formSignUp.postal_code === ""
    ) {
      return false;
    }
    return true;
  };

  const nextStep = async () => {
    setLoading(true);
    const check = await checkEmail();
    if (!validateForm()) {
      Helpers.notification.warning(
        "Oops! It seems some required fields are missing. Please fill them out"
      );
      setLoading(false);
      return;
    }
    if (!Validates.validateValidEmail(formSignUp.email)) {
      Helpers.notification.warning("Email is invalid");
      setLoading(false);
      return;
    }
    if (check) {
      Helpers.notification.warning("Email is already exists");
      setLoading(false);
      return;
    }
    if (googleId !== null) {
      setLoading(false);
      setCurrentStep(3);
    } else {
      setLoading(false);
      setCurrentStep(2);
    }
    //setLoading(false);
    //setCurrentStep(2);
  };

  const checkEmail = async () => {
    var check = false;
    try {
      const response = await userApi.checkEmail(formSignUp.email);
      if (response.status === 200 && response.data.exist === true) {
        check = true;
      }
      return check;
    } catch (error) {
      Helpers.notification.warning("Can not check email. Please try again");
      errorLog("InfoForm.tsx - checkEmail", error);
    }
  };

  const handleSeletedLocation = async (item: any) => {
    setIsOpenSuggestion(false);
    const zipCode = await Helpers.fetchZipCode(item.lat, item.lon);
    setSelectedLocation({ ...item, postCode: zipCode });
  };

  useEffect(() => {
    if (Object.keys(selectedLocation).length > 0) {
      setFormSignUp({
        ...formSignUp,
        postal_code: selectedLocation.postCode,
        street: selectedLocation.address.road
          ? selectedLocation.address.road
          : selectedLocation.display_name,
        city: selectedLocation.address.city
          ? selectedLocation.address.city
          : selectedLocation.address.town
          ? selectedLocation.address.town
          : selectedLocation.address.county
          ? selectedLocation.address.county
          : selectedLocation.address.village,
        province: selectedLocation.address.province
          ? selectedLocation.address.province
          : selectedLocation.address.state
          ? selectedLocation.address.state
          : selectedLocation.address.city,
        lat: selectedLocation.lat,
        lon: selectedLocation.lon,
      });
    }
  }, [selectedLocation]);

  return (
    <div className="InfoForm">
      {!googleId && (
        <>
          <Button
            onClick={() => googleLoginHook()}
            className="google-login-btn"
            icon={<img src={googleLogo} alt="google" />}
          >
            Sign up with Google
          </Button>
          <div className="divider-wrap">
            <Divider className="signin-divider" plain>
              or Sign Up with email
            </Divider>
          </div>
        </>
      )}
      <Row gutter={12} className="mb-3">
        <Col span={12}>
          <div className="forn-group">
            <div className="title">
              First name <span className="text-danger">*</span>
            </div>
            <Input
              className="ant-form"
              variant="filled"
              size="large"
              value={Validates.validateTextForm(formSignUp.first_name)}
              onChange={(e) =>
                handleSetFormSignUp("first_name", e.target.value)
              }
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="forn-group">
            <div className="title">
              Last name <span className="text-danger">*</span>
            </div>
            <Input
              className="ant-form"
              variant="filled"
              size="large"
              value={Validates.validateTextForm(formSignUp.last_name)}
              onChange={(e) => handleSetFormSignUp("last_name", e.target.value)}
            />
          </div>
        </Col>
      </Row>
      <div className="forn-group mb-3">
        <div className="title">
          Email <span className="text-danger">*</span>
        </div>
        <Input
          className="ant-form"
          variant="filled"
          size="large"
          value={formSignUp.email}
          onChange={(e) => handleSetFormSignUp("email", e.target.value)}
        />
      </div>
      <div className="forn-group mb-3">
        <div className="title">Phone</div>
        <Input
          className="ant-form"
          variant="filled"
          size="large"
          value={formSignUp.phone}
          onChange={(e) => handleSetFormSignUp("phone", e.target.value)}
        />
      </div>
      <div className="forn-group mb-3 map-search-input">
        <div className="title">
          Primary address <span className="text-danger">*</span>
        </div>
        <Input
          className="ant-form"
          variant="filled"
          size="large"
          value={formSignUp.street}
          onChange={(e) => {
            const query = e.target.value;
            setIsOpenSuggestion(true);
            setAddressSearchFailed(false);
            if (query === "") {
              setIsOpenSuggestion(false);
            }
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
              const result = await Helpers.handleSearchLocation(query);
              setSearchResult(result);
              // An empty list means either "no match" or "the geocoder is
              // unreachable". Both leave the user with manual entry, so say so
              // instead of leaving an open, empty suggestion box.
              setAddressSearchFailed(query !== "" && result.length === 0);
              setIsOpenSuggestion(query !== "" && result.length > 0);
            }, 300);
            handleSetFormSignUp("street", query);
          }}
        />
        {addressSearchFailed && (
          <div className="address-search-hint" role="status">
            Address lookup is unavailable right now. Type the street, city,
            province and postal code below.
          </div>
        )}
        {isOpenSuggestion && (
          <Suggestion
            searchResult={searchResult}
            onSelect={handleSeletedLocation}
          />
        )}
      </div>
      <Row gutter={[12, 12]}>
        <Col span={12}>
          <div className="forn-group">
            <div className="title">Apt, Suite, etc</div>
            <Input
              className="ant-form"
              variant="filled"
              size="large"
              value={formSignUp.apartment}
              onChange={(e) => handleSetFormSignUp("apartment", e.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="forn-group">
            <div className="title">
              City <span className="text-danger">*</span>
            </div>
            <Input
              className="ant-form"
              size="large"
              variant="filled"
              value={formSignUp.city}
              onChange={(e) => handleSetFormSignUp("city", e.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="forn-group">
            <div className="title">
              Province <span className="text-danger">*</span>
            </div>
            <Input
              className="ant-form"
              variant="filled"
              size="large"
              value={formSignUp.province}
              onChange={(e) => handleSetFormSignUp("province", e.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="forn-group mb-3">
            <div className="title">
              Postal/Zip Code <span className="text-danger">*</span>
            </div>
            <Input
              className="ant-form"
              variant="filled"
              size="large"
              value={formSignUp.postal_code}
              onChange={(e) =>
                handleSetFormSignUp("postal_code", e.target.value)
              }
            />
          </div>
        </Col>
      </Row>
      <Button className="next-btn" onClick={nextStep}>
        Next
      </Button>
    </div>
  );
};

export default InfoForm;
